import { Subscription } from 'rxjs';
import { NGXLogger } from 'ngx-logger';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { isPlatformBrowser } from '@angular/common';
import {
    ChangeDetectionStrategy, ChangeDetectorRef,
    Component, EventEmitter, HostListener,
    Input, OnDestroy, OnInit, Output, ViewChild,
    PLATFORM_ID, Inject,
    SimpleChanges,
    OnChanges,
    ElementRef,
    AfterViewInit,
    AfterViewChecked,
} from '@angular/core';
import { Asset, GetEligiblePaymentCapabilitiesQuery, GetProductPageQuery, ProductOption, ProductOptionGroup, ProductVariant } from '../../../common/generated-types';
import { StateService } from '../../../core/providers/state/state.service';
import { ActiveService } from '../../../core/providers/active/active.service';
import { safeJSONParse } from '../../../common/utils/safe-json-parser';
import { resolveTextValuesDeep } from '../../../common/utils/text-value-resolver';
import { KeyPointListData, KeySellingPointData, ProductCustomizationOption, ProductFeatureData, TitleInfo } from '../../../common/interfaces';
import { CarouselArrowType, ProductOptionGroupDisplayType, TemplateDisplaySize } from '../../../common/enums';
import { SeparatedFormatPricePipe } from '../../../shared/pipes/separated-format-price.pip';
import { DataService } from '../../providers/data/data.service';
import { GET_ELIGIBLE_PAYMENT_CAPABILITIES } from '../../../common/graphql/documents.graphql';
import { take } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { CombinedAnalyticsService } from '../../providers/analytics/combined-analytics.service';
import { FALLBACK_ASSET_URL } from '../../../common/constants';

type Variant = NonNullable<GetProductPageQuery['productPage']['productInfo']>['variants'][number];

interface DisplayOptionGroup {
    group: ProductOptionGroup;
    priority: number;
    displayType: ProductOptionGroupDisplayType;
    options: ProductOption[];
    selectedOptionCode: string | null;
}

@Component({
    selector: 'vsf-product-info',
    templateUrl: './product-info.component.html',
    styleUrls: ['./product-info.component.scss'],
    animations: [
        trigger('expandCollapse', [
            state('collapsed', style({ height: '0', overflow: 'hidden' })),
            state('expanded', style({ height: '*' })),
            transition('collapsed <=> expanded', animate('300ms ease-in-out'))
        ])
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductInfoComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit, AfterViewChecked {
    @Input() selectedVariant: Variant;
    @Input() isMobile: boolean;
    @Input() product: GetProductPageQuery['productPage']['productInfo'];
    @Input() isProductPage: boolean = true;
    @Input() title: string = '';
    @Input() subtitle: string = '';
    @Input() arrowType: CarouselArrowType;

    @Input() displayThumbnails: boolean = true;
    @Input() displayFeaturedReview: boolean = true;
    @Input() displayProductSubtitle: boolean = true;
    @Input() displaySku: boolean = true;
    @Input() displayReviewInfo: boolean = true;
    @Input() displayPaymentCapabilities: boolean = true;
    @Input() displayProductOptions: boolean = true;
    @Input() displayProductDescription: boolean = true;
    @Input() displayAddToCart: boolean = true;
    @Input() displayProductKeyPoints: boolean = true;
    @Input() displayProductFeatures: boolean = true;
    @Input() displayProductPageEntry: boolean = false;

    @Input() linePadding: string;
    @Input() titleInfo: TitleInfo | null = null;
    @Output() variantChanged = new EventEmitter<{ variant: Variant }>();
    @Output() shopNowBannerVisibilityChange: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() printTemplateChanged = new EventEmitter<{ printTemplate: ProductCustomizationOption | null }>();
    @Output() allCustomizationOptionsChanged = new EventEmitter<ProductCustomizationOption[]>();
    @Output() messageValuesChanged = new EventEmitter<{ [optionId: string]: string }>();
    @Output() selectedPaidOptionValuesChanged = new EventEmitter<{ [optionId: string]: string }>();
    @ViewChild('shopNowBanner', { static: false }) shopNowBannerRef: ElementRef<HTMLElement>;

    private lastVisibility: boolean | undefined;
    private sub: Subscription;

    selectedAsset: { id: string; imageUrl: string; };
    selectedPrintTemplate: ProductCustomizationOption | null = null;
    qtyInCart: { [id: string]: number; } = {};
    qty = 1;
    inFlight = false;
    inStock = true;
    selectedVariantMSRP: number | null = null;
    discountPercentage: number | null = null;
    displayOptionGroups: DisplayOptionGroup[] = [];
    variantAssets: Asset[] = [];
    productFeatures: ProductFeatureData[] = [];
    foldedSections: { [key: string]: boolean } = {};
    variantPrintTemplates: ProductCustomizationOption[] = [];
    allCustomizationOptions: ProductCustomizationOption[] = [];
    messageValues: { [optionId: string]: string } = {};
    selectedPaidOptionValues: { [optionId: string]: string } = {};
    templateDisplaySize: TemplateDisplaySize = TemplateDisplaySize.Standard;
    keySellingPointList: KeyPointListData | null = null;
    paymentCapabilities = [];
    shopHost: string = '';
    private fmt: SeparatedFormatPricePipe;
    private lastTrackedVariantId: string | null = null;

    constructor(
        @Inject(PLATFORM_ID) private platformId: object,
        private stateService: StateService,
        private activeService: ActiveService,
        private analyticsService: CombinedAnalyticsService,
        private changeDetector: ChangeDetectorRef,
        private dataService: DataService,
        private route: ActivatedRoute,
        private logger: NGXLogger) {
    }

    ngOnInit() {
        this.fmt = new SeparatedFormatPricePipe(this.changeDetector, this.stateService);
        const shopHost$ = this.stateService.select(state => state.shopHost).pipe(take(1)).subscribe(host => {
            this.shopHost = host || '';
        });
        this.sub = this.activeService.activeOrder$.subscribe(order => {
            this.qtyInCart = {};
            for (const line of order?.lines ?? []) {
                this.qtyInCart[line.productVariant.id] = line.quantity;
            }
            this.changeDetector.markForCheck();
        });
        if(this.displayPaymentCapabilities) {
            this.dataService.query<GetEligiblePaymentCapabilitiesQuery>(GET_ELIGIBLE_PAYMENT_CAPABILITIES)
            .pipe(take(1))
            .subscribe((response:any) => {
                if (response?.eligiblePaymentCapabilities) {
                    this.paymentCapabilities = response.eligiblePaymentCapabilities;
                    this.logger.debug('Payment capabilities:', this.paymentCapabilities);
                } else {
                    this.logger.warn('No eligible payment capabilities found.');
                }
            }
            , error => {
                this.logger.error('Error fetching eligible payment capabilities:', error);
            });
        }
    }

    ngOnDestroy() {
        if (this.sub) {
            this.sub.unsubscribe();
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.product) {
            this.initializeComponentState();
        }
        if (changes.selectedVariant && !changes.selectedVariant.firstChange && !changes.product) {
            const newVariant = changes.selectedVariant.currentValue as Variant | undefined;
            if (newVariant) {
                // Update state, but DO NOT emit variantChanged (to avoid loops)
                this.selectedVariant = newVariant;
                this.updateStateForSelectedVariant(newVariant);
                this.updateVariantAssets();
                this.updateSelectedAsset();
                this.updatePrintTemplates();
                this.selectedPrintTemplate = this.variantPrintTemplates.length > 0 ? this.variantPrintTemplates[0] : null;
                this.printTemplateChanged.emit({ printTemplate: this.selectedPrintTemplate });

                this.trackVariantView(newVariant);
                this.changeDetector.markForCheck();
            }
        }
    }

    ngAfterViewInit() {
        this.checkShopNowBannerVisibility();
    }

    ngAfterViewChecked() {
        this.checkShopNowBannerVisibility();
    }

    private checkShopNowBannerVisibility() {
        if (isPlatformBrowser(this.platformId)) {
            if (this.shopNowBannerRef && this.shopNowBannerRef.nativeElement) {
                const rect = this.shopNowBannerRef.nativeElement.getBoundingClientRect();
                const isScreenBelowBanner = rect.bottom < 0;
                if (isScreenBelowBanner !== this.lastVisibility) {
                    this.shopNowBannerVisibilityChange.emit(!isScreenBelowBanner);
                    this.lastVisibility = isScreenBelowBanner;
                    this.changeDetector.markForCheck();
                }
            } else if (this.lastVisibility !== undefined) {
                this.shopNowBannerVisibilityChange.emit(true);
                this.lastVisibility = undefined;
                this.changeDetector.markForCheck();
            }
        }
    }

    @HostListener('window:scroll', [])
    onWindowScroll() {
        this.checkShopNowBannerVisibility();
    }

    private initializeComponentState() {
        this.logger.debug('Initializing component state...');
        if (!this.product) {
            this.logger.warn('Product data not available for initialization.');
            return;
        }
        // --- Reset State ---
        this.productFeatures = [];
        this.foldedSections = {};
        this.selectedVariantMSRP = null;
        this.discountPercentage = null;
        this.inStock = true;
        this.displayOptionGroups = [];
        this.variantAssets = [];
        this.lastTrackedVariantId = null;
        // --- Initialize Product Features ---
        if (this.product.customFields?.ProductFeatures) {
            this.product.customFields.ProductFeatures.forEach((feature: string) => {
                const productFeature = safeJSONParse<ProductFeatureData>(feature, this.logger);
                if (productFeature) {
                    this.productFeatures.push(productFeature);
                    this.foldedSections[productFeature.title] = true;
                }
            });
        }
        if (this.product?.customFields?.productKeySellingPointList) {
            const raw = safeJSONParse<KeyPointListData>(this.product.customFields.productKeySellingPointList, this.logger);
            this.keySellingPointList = raw ? resolveTextValuesDeep(raw) : null;
        }
        // --- Initialize and Sort Option Groups ---
        this.initializeAndSortOptionGroups();
        this.setInitialOptionsAndSelections();
        // --- Update Variant Based on Initial Selections (if not provided) ---
         if (!this.selectedVariant && this.product.variants.length > 0) {
            this.logger.debug('No initial variant provided, updating based on default selections.');
            this.updateSelectedVariant();
        } else if (this.selectedVariant) {
            this.logger.debug('Initial variant provided, ensuring state matches.', this.selectedVariant.id);
            this.updateStateForSelectedVariant(this.selectedVariant);
            this.trackVariantView(this.selectedVariant);
        }
        // --- Update Variant Assets ---
        this.updateVariantAssets();
        this.updateSelectedAsset();
        this.updatePrintTemplates();
        const urlTemplateId = this.route.snapshot.queryParamMap.get('templateId');
        if (urlTemplateId && this.variantPrintTemplates.length > 0) {
            const requestedTemplate = this.variantPrintTemplates.find(t => t.id === urlTemplateId);
            if (requestedTemplate) {
                this.selectedPrintTemplate = requestedTemplate;
            } else {
                this.selectedPrintTemplate = this.variantPrintTemplates[0];
            }
        } else {
            this.selectedPrintTemplate = this.variantPrintTemplates.length > 0 ? this.variantPrintTemplates[0] : null;
        }
        this.printTemplateChanged.emit({ printTemplate: this.selectedPrintTemplate });
        this.inStock = this.selectedVariant ? this.selectedVariant.stockLevel !== 'OUT_OF_STOCK' : true;
        this.templateDisplaySize = this.selectedVariant?.customFields?.customizationTemplateDisplaySize as TemplateDisplaySize
             || this.product.customFields?.customizationTemplateDisplaySize as TemplateDisplaySize
             || TemplateDisplaySize.Standard;

        this.changeDetector.markForCheck();
        this.logger.debug('Component state initialization complete.');
    }

    private initializeAndSortOptionGroups() {
        if (!this.product?.optionGroups || this.product.optionGroups.length === 0) {
            this.displayOptionGroups = [];
            return;
        }
        const sortedGroups = [...this.product.optionGroups].sort((a, b) => {
            const priorityA = a.customFields?.priority ?? Infinity;
            const priorityB = b.customFields?.priority ?? Infinity;
            return priorityA - priorityB;
        });
        this.displayOptionGroups = sortedGroups.map(group => {
            const displayType = group.customFields?.displayType || ProductOptionGroupDisplayType.Options;
            return {
                group: group as ProductOptionGroup,
                priority: group.customFields?.priority ?? Infinity,
                displayType: displayType,
                options: [],
                selectedOptionCode: null,
            } as DisplayOptionGroup;
        });
        this.logger.debug('Sorted display option groups:', this.displayOptionGroups.map(d => ({ name: d.group.name, priority: d.priority, type: d.displayType })));
    }

    private setInitialOptionsAndSelections() {
        if (this.displayOptionGroups.length === 0) return;
        if (this.selectedVariant) {
            this.logger.debug('Using provided selectedVariant to set initial options:', this.selectedVariant.id);
            this.selectedVariant.options.forEach(variantOption => {
                const groupIndex = this.displayOptionGroups.findIndex(dg => dg.group.id === variantOption.groupId);
                if (groupIndex !== -1) {
                    this.displayOptionGroups[groupIndex].selectedOptionCode = variantOption.code;
                    this.logger.debug(`Set initial selection for group ${this.displayOptionGroups[groupIndex].group.name}: ${variantOption.code}`);
                }
            });
        }
        // Populate available options for ALL groups based on HIGHER priority selections
         for (let i = 0; i < this.displayOptionGroups.length; i++) {
            this.updateAvailableOptionsForGroup(i);
            if (!this.displayOptionGroups[i].selectedOptionCode && this.displayOptionGroups[i].options.length > 0) {
                this.displayOptionGroups[i].selectedOptionCode = this.displayOptionGroups[i].options[0].code;
                this.logger.debug(`Default selected first available option for group ${this.displayOptionGroups[i].group.name}: ${this.displayOptionGroups[i].selectedOptionCode}`);
            }
        }
        this.logger.debug('Final initial displayOptionGroups state:', JSON.parse(JSON.stringify(this.displayOptionGroups))); // Deep copy for logging
    }

    // handler for changing any option
    onOptionChange(groupIndex: number, newOptionCode: string) {
        const changedGroup = this.displayOptionGroups[groupIndex];
        if (changedGroup.selectedOptionCode === newOptionCode) {
            this.logger.debug('Selection hasn\'t changed, skipping update.');
            return;
       }
       this.logger.debug(`Option change in group ${changedGroup.group.name} (index ${groupIndex}): ${newOptionCode}`);
        changedGroup.selectedOptionCode = newOptionCode;
        for (let i = groupIndex + 1; i < this.displayOptionGroups.length; i++) {
            this.updateAvailableOptionsForGroup(i);

            const currentSelection = this.displayOptionGroups[i].selectedOptionCode;
            const isStillAvailable = this.displayOptionGroups[i].options.some(opt => opt.code === currentSelection);
            if (currentSelection && !isStillAvailable) {
                this.logger.debug(`Resetting selection for lower priority group ${this.displayOptionGroups[i].group.name} as ${currentSelection} is no longer available.`);
                this.displayOptionGroups[i].selectedOptionCode = this.displayOptionGroups[i].options.length > 0
                    ? this.displayOptionGroups[i].options[0].code
                    : null;
            } else if (!currentSelection && this.displayOptionGroups[i].options.length > 0) {
                this.logger.debug(`Selecting first available option for lower priority group ${this.displayOptionGroups[i].group.name}: ${this.displayOptionGroups[i].selectedOptionCode}`);
                this.displayOptionGroups[i].selectedOptionCode = this.displayOptionGroups[i].options[0].code;
            }
        }
        this.updateSelectedVariant();
        this.changeDetector.markForCheck();
        this.logger.debug('Option change processing complete. Updated displayOptionGroups:', JSON.parse(JSON.stringify(this.displayOptionGroups)));
    }

    onPrintTemplateChange(newTemplate: ProductCustomizationOption): void {
        if (this.selectedPrintTemplate?.id === newTemplate.id) {
            return;
        }
        this.selectedPrintTemplate = newTemplate;
        this.printTemplateChanged.emit({ printTemplate: newTemplate });
        this.changeDetector.markForCheck();
    }

    onMessageChange(optionId: string, value: string): void {
        this.messageValues = { ...this.messageValues, [optionId]: value };
        this.messageValuesChanged.emit(this.messageValues);
        this.changeDetector.markForCheck();
    }

    onPaidOptionChange(optionId: string, key: string): void {
        this.selectedPaidOptionValues = { ...this.selectedPaidOptionValues, [optionId]: key };
        this.selectedPaidOptionValuesChanged.emit(this.selectedPaidOptionValues);
        this.changeDetector.markForCheck();
    }

    // Updates the available options for a specific group based on selections in higher priority groups
    private updateAvailableOptionsForGroup(groupIndex: number) {
        const targetGroup = this.displayOptionGroups[groupIndex];
        const higherPriorityFilters = this.displayOptionGroups
            .slice(0, groupIndex)
            .filter(dg => dg.selectedOptionCode !== null)
            .map(dg => ({
                groupId: dg.group.id,
                code: dg.selectedOptionCode as string
            }));
        const potentiallyMatchingVariants = this.filterVariantsByOptions(higherPriorityFilters);
        this.logger.debug(`Found ${potentiallyMatchingVariants.length} variants matching higher priority selections.`);

        const availableOptionCodes = new Set<string>();
        potentiallyMatchingVariants.forEach(variant => {
            variant.options.forEach(option => {
                if (option.groupId === targetGroup.group.id) {
                    availableOptionCodes.add(option.code);
                }
            });
        });
        this.logger.debug(`Available option codes for ${targetGroup.group.name}:`, Array.from(availableOptionCodes));

        targetGroup.options = targetGroup.group.options
            .filter(option => availableOptionCodes.has(option.code))
            .sort((a, b) => {
                const priorityA = a.customFields?.Priority ?? Infinity;
                const priorityB = b.customFields?.Priority ?? Infinity;
                return priorityA - priorityB;
            });
        this.logger.debug(`Final sorted available options for ${targetGroup.group.name}:`, targetGroup.options.map(o => o.code));
    }

    // Finds the single product variant matching ALL current selections
    private updateSelectedVariant() {
        if (this.displayOptionGroups.length === 0 && this.product?.variants.length === 1) {
            this.logger.debug('No option groups, selecting the only variant.');
            this.handleVariantChange(this.product.variants[0]);
            return;
        }
        const allSelectedFilters = this.displayOptionGroups
            .filter(dg => dg.selectedOptionCode !== null)
            .map(dg => ({
                groupId: dg.group.id,
                code: dg.selectedOptionCode as string
            }));
        const matchingVariants = this.filterVariantsByOptions(allSelectedFilters);
        if (matchingVariants.length === 1) {
            this.handleVariantChange(matchingVariants[0]);
        } else if (matchingVariants.length === 0) {
             this.logger.error('No variant found matching the current selections!', allSelectedFilters);
             this.handleVariantChange(undefined);
        } else {
            this.logger.warn('Multiple variants found matching the current selections. Using defaultVariant or the first one.', matchingVariants.map(v => v.id));
            let defaultVariant = matchingVariants[0];
            if(this.product?.customFields?.defaultVariant?.id) {
                defaultVariant = matchingVariants.find(v => v.id === this.product?.customFields?.defaultVariant?.id) || defaultVariant;
            }
            this.handleVariantChange(defaultVariant);
        }
    }

     // Helper to handle the consequences of a variant change (or becoming undefined)
     private handleVariantChange(newVariant: Variant | undefined) {
        const previousVariantId = this.selectedVariant?.id;
        this.selectedVariant = newVariant as ProductVariant;
        if(newVariant){
            this.logger.debug(`Variant changed from ${previousVariantId || 'none'} to ${newVariant.id}`);
            this.updateStateForSelectedVariant(newVariant);
            this.updateVariantAssets();
            this.updateSelectedAsset();
            if (previousVariantId !== newVariant.id) {
                this.variantChanged.emit({ variant: newVariant });
            }
            this.updatePrintTemplates();
            this.selectedPrintTemplate = this.variantPrintTemplates.length > 0 ? this.variantPrintTemplates[0] : null;
            this.printTemplateChanged.emit({ printTemplate: this.selectedPrintTemplate });
            this.trackVariantView(newVariant);
        } else {
            this.logger.debug(`Variant changed from ${previousVariantId || 'none'} to none.`);
            this.variantChanged.emit({ variant: this.selectedVariant });
        }
        this.changeDetector.markForCheck();
    }

    // Helper to update stock, MSRP based on the current selectedVariant
    private updateStateForSelectedVariant(variant: Variant) {
        this.inStock = variant.stockLevel !== 'OUT_OF_STOCK';
        this.selectedVariantMSRP = variant.customFields?.MSRP ?? null;
        if (this.selectedVariantMSRP && this.selectedVariantMSRP > variant.price) {
            this.discountPercentage = ((this.selectedVariantMSRP - variant.price) / this.selectedVariantMSRP) * 100;
        } else {
            this.discountPercentage = null;
            this.selectedVariantMSRP = null;
        }
        this.logger.debug(`State updated for variant ${variant.id}: inStock=${this.inStock}, MSRP=${this.selectedVariantMSRP}, discount=${this.discountPercentage}`);
    }

    private trackVariantView(variant: Variant | undefined) {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }
        if (!variant || !this.product) {
            return;
        }
        if (this.lastTrackedVariantId === variant.id) {
            return;
        }
        this.lastTrackedVariantId = variant.id;
        this.analyticsService.selectItem(this.product as any, variant as any);
        this.analyticsService.viewItem(this.product as any, variant as any);
    }

    private filterVariantsByOptions(filters: { groupId: string; code: string }[]): Variant[] {
        if (!this.product?.variants) {
            return [];
        }
        if (filters.length === 0) {
            return this.product.variants;
        }
        return this.product.variants.filter(variant =>
            filters.every(filter =>
                variant.options.some(opt => opt.groupId === filter.groupId && opt.code === filter.code)
            )
        );
    }

     getVariantImageForTile(groupIndex: number, optionCode: string): Asset | undefined {
        const targetGroup = this.displayOptionGroups[groupIndex];
        const filters = this.displayOptionGroups
            .slice(0, groupIndex)
            .filter(dg => dg.selectedOptionCode !== null)
            .map(dg => ({ groupId: dg.group.id, code: dg.selectedOptionCode! }))
            .concat({ groupId: targetGroup.group.id, code: optionCode }); // Add the tile option itself
        const variants = this.filterVariantsByOptions(filters);
        if (variants.length > 0) {
            return variants[0].featuredAsset as Asset|| variants[0].assets?.[0] || this.product?.featuredAsset || undefined;
        } else {
            return undefined;
        }
    }

    getPriceForOption(groupIdx: number, optionCode: string): number | null {
        const filters = [
          ...this.displayOptionGroups
               .slice(0, groupIdx)
               .filter(g => g.selectedOptionCode)
               .map(g => ({ groupId: g.group.id, code: g.selectedOptionCode! })),
          { groupId: this.displayOptionGroups[groupIdx].group.id, code: optionCode },
        ];
        const variant = this.filterVariantsByOptions(filters)[0];
        return variant ? variant.price : null;
    }

    getOptionTooltip(groupIdx: number, optionCode: string, name: string): string {
        const priceNum = this.getPriceForOption(groupIdx, optionCode);
        if (priceNum == null) { return name; }

        const { currency, price } = this.fmt.transform(priceNum)!;
        return `${name} — ${currency}${price}`;
    }

    private updateVariantAssets() {
        const variantAssets: Asset[] = [];
        this.selectedVariant?.assets?.forEach((asset: any) => {
            variantAssets.push(asset);
        });
        this.product?.assets?.forEach((asset: any) => {
            if(variantAssets.find((variantAsset: any) => variantAsset.id === asset.id)) {
                return;
            }
            variantAssets.push(asset);
        });
        this.variantAssets = variantAssets;
    }

    private updatePrintTemplates() {
        const allOptions: ProductCustomizationOption[] = [];
        this.selectedVariant?.customizationOptions?.forEach((option: any) => {
            allOptions.push(option as ProductCustomizationOption);
        });
        this.product?.customizationOptions?.forEach((productOption: any) => {
            if (!allOptions.find((o: any) => o.id === productOption.id)) {
                allOptions.push(productOption as ProductCustomizationOption);
            }
        });

        this.allCustomizationOptions = allOptions;

        this.variantPrintTemplates = this.allCustomizationOptions.filter(o => o.type === 'custom-print-template');

        // Reset user selections for non-print-template options when variant changes
        this.messageValues = {};
        this.selectedPaidOptionValues = {};
        this.messageValuesChanged.emit(this.messageValues);
        this.selectedPaidOptionValuesChanged.emit(this.selectedPaidOptionValues);

        this.allCustomizationOptionsChanged.emit(this.allCustomizationOptions);
        this.logger.debug('Updated all customization options:', this.allCustomizationOptions);
    }

    // Update the primary selected asset shown (e.g., for the gallery's initial state)
    private updateSelectedAsset() {
        let newSelectedAsset: Asset | undefined;
        if (this.selectedVariant?.featuredAsset) {
            newSelectedAsset = this.selectedVariant.featuredAsset as Asset;
        } else if (this.product?.featuredAsset) {
            newSelectedAsset = this.product.featuredAsset as Asset;
        } else if (this.variantAssets.length > 0) {
            newSelectedAsset = this.variantAssets[0];
        }
        if (newSelectedAsset) {
            this.selectedAsset = { id: newSelectedAsset.id, imageUrl: newSelectedAsset.imageUrl || newSelectedAsset.preview || FALLBACK_ASSET_URL };
        } else {
            this.selectedAsset = { id: '', imageUrl: '' }; // Clear if no assets found
            this.logger.warn('No suitable asset found to set as selectedAsset.');
        }
    }

    viewCartFromNotification(closeFn: () => void) {
        this.stateService.setState('cartDrawerOpen', true);
        closeFn();
    }

    isFolded(sectionId: string): boolean {
        return this.foldedSections[sectionId];
    }

    toggleFold(sectionId: string) {
        this.foldedSections[sectionId] = !this.foldedSections[sectionId];
        this.changeDetector.markForCheck();
    }

    getSku(): string {
        return this.selectedVariant?.sku || '';
    }

    getVariantSubtitle(groupIdx: number, optionCode: string): string | undefined {
        const variant = this.filterVariantsByOptions([
            ...this.displayOptionGroups
                 .slice(0, groupIdx)
                 .filter(g => g.selectedOptionCode)
                 .map(g => ({ groupId: g.group.id, code: g.selectedOptionCode! })),
            { groupId: this.displayOptionGroups[groupIdx].group.id, code: optionCode },
        ])[0];
        return variant?.customFields?.variantSubtitle;  // ← adjust to your real field
    }

    /** MSRP for strike-through price. */
    getMsrpPriceForOption(groupIdx: number, optionCode: string): number | null {
        const variant = this.filterVariantsByOptions([
            ...this.displayOptionGroups
                 .slice(0, groupIdx)
                 .filter(g => g.selectedOptionCode)
                 .map(g => ({ groupId: g.group.id, code: g.selectedOptionCode! })),
            { groupId: this.displayOptionGroups[groupIdx].group.id, code: optionCode },
        ])[0];
        return variant?.customFields?.MSRP ?? null;
    }

    scrollToReviews() {
        if (isPlatformBrowser(this.platformId)) {
            const reviews = document.getElementById('product-reviews');
            if (reviews) {
                reviews.scrollIntoView({ behavior: 'smooth' });
            }
        }
    }

    getSelectedOptionName(groupIndex: number): string | null {
        const group = this.displayOptionGroups[groupIndex];
        if (!group || !group.selectedOptionCode) {
            return null;
        }
        const selectedOption = group.options.find(opt => opt.code === group.selectedOptionCode);
        return selectedOption?.name || group.selectedOptionCode;
    }

    getProductPageUrl(): string {
        if (!this.product) { return '#'; }
        const variantId = this.selectedVariant?.id;
        const qs       = variantId ? `?variantid=${variantId}` : '';
        return `${this.shopHost}/dp/${this.product.slug}${qs}`;
    }
}
